import { ConfigResponse } from "api/shared";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import { useSourceTypeResolver } from "hooks/useSourceTypeResolver";
import { useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { getSourceTypeLinkUrl, usePageViewedEvent } from "utils";

import CentreOverlay from "components/CentreOverlay";
import ErrorFallback from "components/ErrorFallback";
import Landing from "components/Landing";
import Spinner from "components/Spinner";
import { CompanyParams } from "config/urls";

interface CompanyLandingContainerProps extends CompanyParams {
  data: ConfigResponse;
}

export default function CompanyLandingContainer({
  data,
  companyId,
}: CompanyLandingContainerProps) {
  const { sourceTypes, status } = useSourceTypes();
  const [navigateNext, setNavigateNext] = useState("");
  const locationSearch = useLocation().search;
  const { currentSourceType } = useSourceTypeResolver({}, sourceTypes);

  const nextPrioritySourceTypeRedirect = useNextSourceTypeRedirectHandler(
    companyId,
    currentSourceType
  );

  usePageViewedEvent({
    ready: data.showLandingPage,
    data,
  });

  useEffect(() => {
    if (status === SourceTypeStatus.READY && !data.showLandingPage) {
      setNavigateNext(getSourceTypeLinkUrl(companyId, currentSourceType));
    }
  }, [data.showLandingPage, companyId, status, currentSourceType]);

  if (data.disableLink) {
    return (
      <ErrorFallback
        customStatusCode={400}
        customError="Link has been disabled"
      />
    );
  }

  if (navigateNext) {
    return (
      <Redirect
        to={{
          pathname: navigateNext,
          search: locationSearch,
        }}
        push={true}
      />
    );
  }

  if (status === SourceTypeStatus.ERROR) {
    return <ErrorFallback />;
  }

  if (!nextPrioritySourceTypeRedirect) {
    return (
      <CentreOverlay data-testid="company-config-loading">
        <Spinner />
      </CentreOverlay>
    );
  }

  return <Landing data={data} onClickNext={nextPrioritySourceTypeRedirect} />;
}
